import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '../../../models/classes/location';
import { UiService } from '../../../services/ui.service';
import { I18nService } from '../../../services/i18n.service';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { RenameCardHeaderComponent } from '../rename-card-header/rename-card-header';
import { NavButtonComponent } from '../../nav-button/nav-button';
import { AssetNavButtonComponent } from '../../asset-nav-button/asset-nav-button';
import { NameSlicePipe } from '../../../pipes/name-slice.pipe';
import { getIconByEquipmentType } from '../../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';

@Component({
  selector: 'app-collapse-card-layer',
  templateUrl: './collapse-card-layer.html',
  styleUrls: ['./collapse-card-layer.scss'],
  imports: [
    NzCollapseModule,
    RenameCardHeaderComponent,
    NavButtonComponent,
    AssetNavButtonComponent,
    NameSlicePipe,
  ],
})
export class CollapseCardLayerComponent {
  getIconByEquipmentType = getIconByEquipmentType;
  location = input<Location | null>(null);
  contentEditable = input<boolean>(false);
  editObject = input<any | null>(null);

  add = output<Location>();
  addEquipment = output<Location>();
  nameError = output<boolean>();
  editCompleted = output<void>();

  private readonly ui = inject(UiService);
  public readonly i18n = inject(I18nService);
  public readonly router = inject(Router);

  editedObjectId = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);

  // ===== Derived state (Computed) =====
  isMobile = computed<boolean>(() => this.ui.IsMobile());
  isTablet = computed<boolean>(() => this.ui.IsTablet());
  isDesktop = computed<boolean>(() => this.ui.IsDesktop());

  /**
   * Whether the card needs additional bottom padding based on the current `location()` tree.
   * Mirrors original `needsAdditionalPadding()` logic.
   */
  needsAdditionalPadding = computed<boolean>(() => {
    const loc = this.location();
    if (!loc || !loc.children || loc.children.length === GLOBAL_CONSTANT.DEFAULT_INITIALIZER)
      return false;

    const lastChild = loc.children[loc.children.length - 1];
    if (lastChild.children.length === GLOBAL_CONSTANT.DEFAULT_INITIALIZER) {
      if (loc.children.length === 1) return true;

      for (let i = GLOBAL_CONSTANT.DEFAULT_INITIALIZER; i < loc.children.length - 1; i++) {
        const sibling = loc.children[i];
        if (sibling.children.length > 0) {
          if (sibling.children[0].curLayer !== 'h') {
            return false;
          }
        }
      }
      return true;
    }
    return false;
  });

  /** Keep `editedObjectId` in sync with UiService without manual change detection. */
  private readonly editingEffect = effect(() => {
    const id = this.ui.ObjectInEditing();
    if (id == null && !id) return;
    this.editedObjectId.set(id);
  });

  OnEditComplete() {
    this.editCompleted.emit();
  }

  // ===== Handlers =====
  onAdd(location: Location): void {
    this.add.emit(location);
  }

  onAddEquipment(location: Location): void {
    this.addEquipment.emit(location);
  }

  onDelete(locationToDelete: Location): void {
    locationToDelete.delete = true;
    locationToDelete.children = [];
    // (kept behavior) Do not splice from parent’s children here.
  }

  /**
   * Mirrors original `disableCollapseBasedOnSiblings(child)` logic.
   * Kept as a method because it depends on the passed `child`.
   */
  disableCollapseBasedOnSiblings(child: Location): boolean {
    const loc = this.location();
    if (!loc) return true;

    // if siblings
    if (loc.children.length > 1) {
      for (let i = GLOBAL_CONSTANT.DEFAULT_INITIALIZER; i < loc.children.length - 1; i++) {
        const sibling = loc.children[i];
        // if they have children and are not the given child
        if (sibling !== child && sibling.children.length > GLOBAL_CONSTANT.DEFAULT_INITIALIZER) {
          if (sibling.children[0].curLayer !== 'h') {
            return false;
          }
        }
      }
      return true;
    } else {
      // no siblings -> disable if no own children
      return child.children.length === GLOBAL_CONSTANT.DEFAULT_INITIALIZER;
    }
  }

  onNameError(event: boolean): void {
    this.nameError.emit(event);
  }
}
